// 指针输入 (Pointer Events): 选中 / 拖拽 / 落子
// 参考文档: https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events

import { GameState, Piece, Square, Move } from '../game/types';
import { legalPieceMoves } from '../game/rules';
import { BoardLayout, pixelToSquare, squareToPixel } from './renderer';
import { PieceInteractionState } from './pieceInteraction';

export interface InputController {
  detach(): void;
}

export interface InputCallbacks {
  onPickSelected?: (sq: Square | null, legalTargets: Square[]) => void;
  onMove?: (move: Move) => void;
  onDragUpdate?: (drag: { square: Square; piece: Piece; x: number; y: number } | null) => void;
}

// 用户操作颜色: 仅允许此色棋子被拾起. 传 getter 以便开局随机换边.
export function attachInput(
  canvas: HTMLCanvasElement,
  layout: BoardLayout,
  getState: () => GameState,
  getUserColor: () => 'r' | 'b',
  cb: InputCallbacks,
): InputController {
  let selected: Square | null = null;
  let activePiece: Piece | null = null;
  let pointerId: number | null = null;
  const interaction = new PieceInteractionState();

  const rect = () => canvas.getBoundingClientRect();

  function clearSelection(): void {
    selected = null;
    cb.onPickSelected?.(null, []);
  }

  function onDown(e: PointerEvent): void {
    const state = getState();
    const userColor = getUserColor();
    if (state.turn !== userColor) return;
    const flip = userColor === 'b';
    const r = rect();
    const x = (e.clientX - r.left) * (canvas.width / r.width);
    const y = (e.clientY - r.top) * (canvas.height / r.height);
    const sq = pixelToSquare(layout, x, y, flip);
    if (sq === null) return;
    const piece = state.board[sq];
    if (piece && piece.color === userColor) {
      // 选中 + 开始拖拽
      selected = sq;
      pointerId = e.pointerId;
      canvas.setPointerCapture(e.pointerId);
      const targets = legalPieceMoves(state, sq).map(m => m.to);
      cb.onPickSelected?.(sq, targets);
      interaction.press({
        square: sq,
        pointerCss: { x: e.clientX, y: e.clientY },
        pointerCanvas: { x, y },
        pieceCenter: squareToPixel(layout, sq, flip),
      });
      activePiece = piece;
      e.preventDefault();
    } else if (selected !== null) {
      // 已选中, 点击目标格尝试落子
      tryMove(selected, sq);
    }
  }

  function onMove(e: PointerEvent): void {
    if (!activePiece) return;
    const r = rect();
    const x = (e.clientX - r.left) * (canvas.width / r.width);
    const y = (e.clientY - r.top) * (canvas.height / r.height);
    const snapshot = interaction.move(
      { x: e.clientX, y: e.clientY },
      { x, y },
    );
    if (snapshot.phase === 'dragging') {
      cb.onDragUpdate?.({
        square: snapshot.square,
        piece: activePiece,
        x: snapshot.x,
        y: snapshot.y,
      });
      e.preventDefault();
    }
  }

  function onUp(e: PointerEvent): void {
    if (!activePiece || interaction.snapshot().phase === 'idle') return;
    const flip = getUserColor() === 'b';
    const r = rect();
    const x = (e.clientX - r.left) * (canvas.width / r.width);
    const y = (e.clientY - r.top) * (canvas.height / r.height);
    interaction.move({ x: e.clientX, y: e.clientY }, { x, y });
    const released = interaction.release();
    activePiece = null;
    if (released.phase === 'dragging') cb.onDragUpdate?.(null);
    if (pointerId !== null) {
      try { canvas.releasePointerCapture(pointerId); } catch { /* ignore */ }
      pointerId = null;
    }
    if (released.phase === 'dragging') {
      const target = pixelToSquare(layout, released.x, released.y, flip);
      if (target !== null && target !== released.square) {
        tryMove(released.square, target);
      }
    }
  }

  function onCancel(): void {
    interaction.cancel();
    activePiece = null;
    pointerId = null;
    cb.onDragUpdate?.(null);
  }

  function tryMove(from: Square, to: Square): void {
    const state = getState();
    const moves = legalPieceMoves(state, from);
    const found = moves.find(m => m.to === to);
    if (found) {
      cb.onMove?.(found);
      clearSelection();
    }
    // 若未成功落子, 保持选中 (允许再次点击)
  }

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onCancel);

  return {
    detach() {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      interaction.cancel();
      clearSelection();
    },
  };
}
